import React, { useState, useMemo, useCallback } from "react";
import { DrilldownList, SearchActionsToolbar } from "@/components/UI/DrilldownList";
import { PanelItem } from "@/components/inputs/DynamicTagsInputs/FilterInput/comps/DocHierarchyFilter/comps/PanelItem";
import { useLightbox } from "@/components/UI/lightbox";
import { StyledOuterWrapper } from "../styled";
import { ScrollWrapper, SectionLabel, SectionDivider } from "./PanelChrome";

/**
 * Recursive renderers for the cascade picker, co-located in one module so the
 * mutual recursion (PanelRows → FolderRow → FolderPanel → PanelRows) needs no
 * circular imports.
 *
 * Single-select throughout: clicking a leaf emits the ORIGINAL option object
 * (via `onSelect`); the caller closes the picker.
 */

/** Flat single-select list of leaf rows. Emits the ORIGINAL option on click. */
export const LeafList = ({ leaves = [], value, onSelect, emptyMessage }) => {
  const options = useMemo(
    () => leaves.map((l) => ({ ...l.content, __option: l.option, hasChildren: false })),
    [leaves]
  );
  const drilldownValue = useMemo(
    () => (value ? [{ value: value.value }] : []),
    [value]
  );
  const handleChange = useCallback((opt) => onSelect?.(opt.__option), [onSelect]);

  return (
    <DrilldownList
      options={options}
      value={drilldownValue}
      isMulti={false}
      onChange={handleChange}
      emptyMessage={emptyMessage}
    />
  );
};

/** A folder row → opens a nested FolderPanel with the folder's child rows.
 *  Marked (isSelected) when it's the current selection or on the path to it.
 *
 *  When the folder carries a `selectOption` (Thematics/Attributes), the ROW BODY
 *  is selectable — clicking it emits the whole-role dimension via `onSelect`, while
 *  the chevron still drills into the individual sub-themes. PanelItem does the
 *  body-selects / arrow-drills split when given `onSelect`; exactly one happens per
 *  click. Folders without a selectOption (datasets, Connected Datasets) stay drill-only. */
const FolderRow = ({ row, value, onSelect, selectedPathKeys }) => {
  const selectOption = row.selectOption;
  const isFolderSelected = !!selectOption && value?.value === selectOption.value;
  return (
    <PanelItem
      option={{ display: row.display, icon: row.icon, hasChildren: true }}
      isSelected={isFolderSelected || selectedPathKeys?.has(row.key)}
      onSelect={selectOption ? () => onSelect?.(selectOption) : undefined}
    >
      <FolderPanel
        title={row.display}
        rows={row.children}
        value={value}
        onSelect={onSelect}
        selectedPathKeys={selectedPathKeys}
      />
    </PanelItem>
  );
};

/**
 * Render an ordered list of rows (folders, leaf runs, labels). Recursive:
 * folder rows open nested FolderPanels, so the Connected-Datasets rollup tree
 * (and Thematics/Attributes) nest to arbitrary depth. Consecutive leaf rows
 * collapse into a single DrilldownList to preserve drag/keyboard behaviour.
 */
export const PanelRows = ({ rows = [], value, onSelect, selectedPathKeys }) => {
  const chunks = useMemo(() => {
    const out = [];
    let leafBuf = [];
    const flush = () => {
      if (leafBuf.length) {
        out.push({ type: "leaves", leaves: leafBuf });
        leafBuf = [];
      }
    };
    for (const row of rows) {
      if (row.type === "leaf") {
        leafBuf.push(row);
        continue;
      }
      flush();
      if (row.type === "folder") out.push({ type: "folder", row });
      else if (row.type === "label") out.push({ type: "label", row });
    }
    flush();
    return out;
  }, [rows]);

  return chunks.map((chunk, i) => {
    if (chunk.type === "folder") {
      return (
        <FolderRow
          key={chunk.row.key}
          row={chunk.row}
          value={value}
          onSelect={onSelect}
          selectedPathKeys={selectedPathKeys}
        />
      );
    }
    if (chunk.type === "label") {
      return (
        <React.Fragment key={chunk.row.key}>
          <SectionDivider />
          <SectionLabel>{chunk.row.text}</SectionLabel>
        </React.Fragment>
      );
    }
    return <LeafList key={`leaves-${i}`} leaves={chunk.leaves} value={value} onSelect={onSelect} />;
  });
};

/**
 * Generic folder panel opened by a folder row (dataset → fields + Connected
 * Datasets, Thematics/Attributes → sub-themes, connected dataset → its fields).
 * Self-contained: owns its own search over its DIRECT leaf rows (folder rows
 * always stay visible so you can keep drilling), like DocHierarchyFilter's
 * DatasetPanel.
 *
 * @param {string}   title     folder label shown atop the panel
 * @param {Array}    rows      Row[] from buildCascadeTree (leaves/folders/labels)
 * @param {Object}   value     currently selected ORIGINAL option (for highlight)
 * @param {Function} onSelect  (originalOption) => void
 */
export const FolderPanel = ({ title, rows = [], value, onSelect, selectedPathKeys }) => {
  const { setOpen } = useLightbox() || {};
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    if (!term) return rows;
    // Filter leaves by display/desc; keep folders + labels so drilling still works.
    return rows.filter((r) => {
      if (r.type !== "leaf") return true;
      const display = (r.content.display || "").toLowerCase();
      const desc = (r.content.desc || "").toLowerCase();
      return display.includes(term) || desc.includes(term);
    });
  }, [rows, term]);

  return (
    <StyledOuterWrapper>
      <SearchActionsToolbar
        search={{ value: search, onChange: setSearch }}
        onClose={setOpen ? () => setOpen(false) : undefined}
      />
      <ScrollWrapper style={{ flex: 1, marginTop: 4 }}>
        {title && <SectionLabel>{title}</SectionLabel>}
        <PanelRows rows={filteredRows} value={value} onSelect={onSelect} selectedPathKeys={selectedPathKeys} />
      </ScrollWrapper>
    </StyledOuterWrapper>
  );
};
